import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Settings, Save } from 'lucide-react';
import { usePersonaConfig, BotPersonaConfig } from '../../hooks/usePersonaConfig';
import { useProdutoConfig, ProdutoConfig } from '../../hooks/useProdutoConfig';
import { useEmojisConfig } from '../../hooks/useEmojisConfig';
import type { ConfigPanelProps } from './types';

const PanelContainer = styled.div<{ $minimized: boolean }>`
  width: 100%;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-top: 2px solid ${({ theme }) => theme.colors.border};
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.easeInOut};
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  cursor: pointer;
  user-select: none;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.background};
  }
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:hover {
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PanelContentWrapper = styled(motion.div)`
  overflow: hidden;
  will-change: height;
`;

const PanelContentInner = styled(motion.div)`
  padding: ${({ theme }) => theme.spacing[6]};
`;

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${({ theme }) => theme.spacing[6]};

  @media (max-width: 1600px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const SectionLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// ===== CUSTOM DROPDOWN ESTILIZADO =====
const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled(motion.button)<{ $isOpen: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ $isOpen, theme }) => $isOpen ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.backgroundSecondary};
  }

  svg {
    transition: transform 0.2s ease;
    transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    color: ${({ theme }) => theme.colors.textTertiary};
  }
`;

const SelectDropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  z-index: 1000;
`;

const SelectOption = styled(motion.button)<{ $isActive: boolean }>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[3]};
  background: ${({ $isActive, theme }) => $isActive ? `${theme.colors.primary}20` : 'transparent'};
  border: none;
  color: ${({ $isActive, theme }) => $isActive ? theme.colors.primary : theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ $isActive }) => $isActive ? '600' : '400'};
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}15;
    color: ${({ theme }) => theme.colors.primary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  margin-left: auto;
  box-shadow: 0 0 8px ${({ theme }) => theme.colors.primary}80;
`;

const Slider = styled.input`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.2);
    }
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const SliderValue = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
`;

const FormalityDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-top: ${({ theme }) => theme.spacing[1]};
  font-style: italic;
`;

// Descrições para cada nível de formalidade
const FORMALITY_DESCRIPTIONS: Record<number, { label: string; description: string }> = {
  0: { label: 'Ultra Informal', description: 'Gírias, abreviações, linguagem de rua' },
  1: { label: 'Muito Informal', description: 'Coloquial com emojis frequentes' },
  2: { label: 'Informal', description: 'Descontraído, amigável, usa "vc", "tb"' },
  3: { label: 'Casual', description: 'Natural mas respeitoso, poucos emojis' },
  4: { label: 'Neutro Casual', description: 'Equilíbrio entre casual e profissional' },
  5: { label: 'Neutro', description: 'Padrão, nem formal nem informal' },
  6: { label: 'Neutro Formal', description: 'Levemente profissional' },
  7: { label: 'Semi-Formal', description: 'Profissional mas acessível' },
  8: { label: 'Formal', description: 'Profissional, vocabulário culto' },
  9: { label: 'Muito Formal', description: 'Corporativo, sem contrações' },
  10: { label: 'Ultra Formal', description: 'Institucional, linguagem jurídica' },
};

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) => theme.colors.border};
    transition: 0.3s;
    border-radius: 26px;

    &:before {
      position: absolute;
      content: '';
      height: 18px;
      width: 18px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }

  input:checked + span {
    background-color: ${({ theme }) => theme.colors.success};
  }

  input:checked + span:before {
    transform: translateX(24px);
  }
`;

const TagInput = styled.input`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  width: 100%;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-top: ${({ theme }) => theme.spacing[2]};
  max-height: 80px;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[1]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}30;
  }
`;

const SaveIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.success}20;
  color: ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

/**
 * Painel de configurações estáticas do bot
 * Minimizável, auto-save com debounce, layout horizontal compacto
 */
export const ConfigPanel: React.FC<ConfigPanelProps> = ({
  userId,
  isMinimized,
  onToggleMinimize,
}) => {
  const { persona, savePersona } = usePersonaConfig(userId);
  const { produto, saveProduto } = useProdutoConfig(userId);
  const { emojis } = useEmojisConfig(userId);

  const [localPersona, setLocalPersona] = useState<BotPersonaConfig>(persona);
  const [localProduto, setLocalProduto] = useState<ProdutoConfig>(produto);
  const [isGeneroOpen, setIsGeneroOpen] = useState(false);
  const [newProibida, setNewProibida] = useState('');
  const [showSaved, setShowSaved] = useState(false);

  // Sincronizar com mudanças remotas
  useEffect(() => {
    setLocalPersona(persona);
  }, [persona]);

  useEffect(() => {
    setLocalProduto(produto);
  }, [produto]);

  // Auto-save com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (JSON.stringify(localPersona) !== JSON.stringify(persona)) {
        savePersona(localPersona);
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 2000);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [localPersona, persona, savePersona]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (JSON.stringify(localProduto) !== JSON.stringify(produto)) {
        saveProduto(localProduto);
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 2000);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [localProduto, produto, saveProduto]);

  const handleAddProibida = () => {
    if (newProibida.trim() && localProduto.palavrasProibidas) {
      setLocalProduto({
        ...localProduto,
        palavrasProibidas: [...localProduto.palavrasProibidas, newProibida.trim()],
      });
      setNewProibida('');
    }
  };

  const handleRemoveProibida = (index: number) => {
    const novas = [...(localProduto.palavrasProibidas || [])];
    novas.splice(index, 1);
    setLocalProduto({ ...localProduto, palavrasProibidas: novas });
  };

  return (
    <PanelContainer $minimized={isMinimized}>
      <PanelHeader onClick={onToggleMinimize}>
        <PanelTitle>
          <Settings size={20} />
          Configurações Estáticas
        </PanelTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AnimatePresence>
            {showSaved && (
              <SaveIndicator
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <Save size={16} />
                Salvo automaticamente
              </SaveIndicator>
            )}
          </AnimatePresence>
          <ToggleButton>
            {isMinimized ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {isMinimized ? 'Expandir' : 'Minimizar'}
          </ToggleButton>
        </div>
      </PanelHeader>

      <AnimatePresence initial={false}>
        {!isMinimized && (
          <PanelContentWrapper
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              mass: 0.8,
            }}
          >
            <PanelContentInner
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 25,
                delay: 0.05,
              }}
            >
              <ConfigGrid>
              {/* Gênero */}
              <ConfigSection>
                <SectionLabel>Gênero</SectionLabel>
                <SelectContainer>
                  <SelectButton
                    $isOpen={isGeneroOpen}
                    onClick={() => setIsGeneroOpen(!isGeneroOpen)}
                    type="button"
                  >
                    <span>{localPersona.genero === 'feminino' ? '👩 Feminino' : '👨 Masculino'}</span>
                    <ChevronDown size={16} />
                  </SelectButton>

                  <AnimatePresence>
                    {isGeneroOpen && (
                      <>
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          onClick={() => setIsGeneroOpen(false)}
                          style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 999,
                          }}
                        />
                        <SelectDropdown
                          initial={{ opacity: 0, y: -8, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.96 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                        >
                          <SelectOption
                            $isActive={localPersona.genero === 'feminino'}
                            onClick={() => {
                              setLocalPersona({ ...localPersona, genero: 'feminino' });
                              setIsGeneroOpen(false);
                            }}
                            whileHover={{ x: 2 }}
                          >
                            <span>👩</span> Feminino
                            {localPersona.genero === 'feminino' && <ActiveDot />}
                          </SelectOption>
                          <SelectOption
                            $isActive={localPersona.genero === 'masculino'}
                            onClick={() => {
                              setLocalPersona({ ...localPersona, genero: 'masculino' });
                              setIsGeneroOpen(false);
                            }}
                            whileHover={{ x: 2 }}
                          >
                            <span>👨</span> Masculino
                            {localPersona.genero === 'masculino' && <ActiveDot />}
                          </SelectOption>
                        </SelectDropdown>
                      </>
                    )}
                  </AnimatePresence>
                </SelectContainer>
              </ConfigSection>

              {/* Humor */}
              <ConfigSection>
                <SectionLabel>Uso de Humor</SectionLabel>
                <Toggle>
                  <input
                    type="checkbox"
                    checked={localPersona.usarHumor}
                    onChange={(e) =>
                      setLocalPersona({ ...localPersona, usarHumor: e.target.checked })
                    }
                  />
                  <span />
                </Toggle>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  {localPersona.usarHumor ? 'Ativado' : 'Desativado'}
                </div>
              </ConfigSection>

              {/* Formalidade */}
              <ConfigSection>
                <SectionLabel>Nível de Formalidade</SectionLabel>
                <Slider
                  type="range"
                  min="0"
                  max="10"
                  value={localPersona.nivelFormalidade}
                  onChange={(e) =>
                    setLocalPersona({ ...localPersona, nivelFormalidade: parseInt(e.target.value) })
                  }
                />
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <SliderValue>{localPersona.nivelFormalidade}/10</SliderValue>
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    {FORMALITY_DESCRIPTIONS[localPersona.nivelFormalidade]?.label}
                  </span>
                </div>
                <FormalityDescription>
                  {FORMALITY_DESCRIPTIONS[localPersona.nivelFormalidade]?.description}
                </FormalityDescription>
              </ConfigSection>

              {/* Palavras Proibidas */}
              <ConfigSection>
                <SectionLabel>Palavras/Tópicos Proibidos</SectionLabel>
                <TagInput
                  placeholder="Digite e pressione Enter"
                  value={newProibida}
                  onChange={(e) => setNewProibida(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddProibida();
                    }
                  }}
                />
                {(localProduto.palavrasProibidas || []).length > 0 && (
                  <TagContainer
                    as={motion.div}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.2 }}
                  >
                    {localProduto.palavrasProibidas.map((palavra, idx) => (
                      <Tag key={idx} onClick={() => handleRemoveProibida(idx)}>
                        {palavra} ×
                      </Tag>
                    ))}
                  </TagContainer>
                )}
              </ConfigSection>

              {/* Emojis Permitidos */}
              <ConfigSection>
                <SectionLabel>Emojis Contextuais</SectionLabel>
                <div style={{ fontSize: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {emojis.positivos?.slice(0, 10).map((emoji, idx) => (
                    <span key={idx}>{emoji}</span>
                  ))}
                </div>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                  Total: {emojis.positivos?.length || 0} emojis
                </div>
              </ConfigSection>
              </ConfigGrid>
            </PanelContentInner>
          </PanelContentWrapper>
        )}
      </AnimatePresence>
    </PanelContainer>
  );
};
